import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import styles from './EditionDetailPage.module.css';

interface Edition {
  id: string;
  title: string;
  date: string;
  description?: string;
  cover_url?: string;
}

interface Photo {
  id: string;
  url: string;
  caption?: string;
  type: string;
  filename?: string;
}

interface Attendee {
  id: string;
  name: string;
  company?: string;
  role?: string;
  bio?: string;
  avatar_url?: string;
  linkedin_url?: string;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const EditionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [edition, setEdition] = useState<Edition | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<Attendee | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const [editionRes, photosRes, attendeesRes] = await Promise.all([
          supabase.from('b2b_editions').select('*').eq('id', id).single(),
          supabase.from('b2b_edition_photos').select('*').eq('edition_id', id),
          supabase
            .from('b2b_edition_attendees')
            .select('member_id, b2b_members(id, name, company, role, bio, avatar_url, linkedin_url)')
            .eq('edition_id', id),
        ]);

        if (editionRes.data) setEdition(editionRes.data);
        if (photosRes.data) setPhotos(photosRes.data);
        if (attendeesRes.data) {
          const members = attendeesRes.data
            .map((a: Record<string, unknown>) => a.b2b_members as unknown as Attendee)
            .filter(Boolean);
          setAttendees(members);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const galleryPhotos = photos.filter((p) => p.type === 'photo');
  const documents = photos.filter((p) => p.type === 'pdf');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight' && lightboxIndex < galleryPhotos.length - 1) {
        setLightboxIndex(lightboxIndex + 1);
      }
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) {
        setLightboxIndex(lightboxIndex - 1);
      }
    },
    [lightboxIndex, galleryPhotos.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p className={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Link to="/members/editions" className={styles.backLink}>
            ← Back to editions
          </Link>
          <p className={styles.loadingText}>Edition not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link to="/members/editions" className={styles.backLink}>
          ← Back to editions
        </Link>

        {edition.cover_url && (
          <div className={styles.coverWrap}>
            <img
              src={edition.cover_url}
              alt={edition.title}
              className={styles.coverImage}
            />
          </div>
        )}

        <span className={styles.dateBadge}>{formatDate(edition.date)}</span>
        <h1 className={styles.title}>{edition.title}</h1>

        {edition.description && (
          <div className={styles.summary}>
            <p className={styles.summaryText}>{edition.description}</p>
          </div>
        )}

        {attendees.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Pr&eacute;sents ({attendees.length})
            </h2>
            <div className={styles.attendeesGrid}>
              {attendees.map((attendee) => (
                <button
                  key={attendee.id}
                  className={styles.attendeeCard}
                  onClick={() => setSelectedProfile(attendee)}
                  type="button"
                >
                  {attendee.avatar_url ? (
                    <img
                      src={attendee.avatar_url}
                      alt={attendee.name}
                      className={styles.attendeeAvatar}
                    />
                  ) : (
                    <div className={styles.attendeeInitials}>
                      {getInitials(attendee.name)}
                    </div>
                  )}
                  <span className={styles.attendeeName}>{attendee.name}</span>
                  {attendee.company && (
                    <span className={styles.attendeeCompany}>{attendee.company}</span>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {documents.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Documents</h2>
            <div className={styles.docsList}>
              {documents.map((doc) => (
                <a
                  key={doc.id}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.docItem}
                >
                  <span className={styles.docIcon}>📄</span>
                  <span className={styles.docName}>{doc.filename || 'Document'}</span>
                  <span className={styles.docAction}>Download</span>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Photos</h2>
          {galleryPhotos.length === 0 ? (
            <p className={styles.loadingText}>Photos coming soon.</p>
          ) : (
            <div className={styles.photosGrid}>
              {galleryPhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  className={styles.photoItem}
                  onClick={() => setLightboxIndex(index)}
                  type="button"
                >
                  <img src={photo.url} alt={photo.caption || ''} />
                  {photo.caption && (
                    <div className={styles.photoCaption}>{photo.caption}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Profile Modal */}
      {selectedProfile && (
        <div
          className={styles.overlay}
          onClick={() => setSelectedProfile(null)}
          role="presentation"
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            {selectedProfile.avatar_url ? (
              <img
                src={selectedProfile.avatar_url}
                alt={selectedProfile.name}
                className={styles.modalAvatar}
              />
            ) : (
              <div className={styles.modalInitials}>
                {getInitials(selectedProfile.name)}
              </div>
            )}
            <h3 className={styles.modalName}>{selectedProfile.name}</h3>
            {selectedProfile.company && (
              <p className={styles.modalCompany}>{selectedProfile.company}</p>
            )}
            {selectedProfile.role && (
              <p className={styles.modalRole}>{selectedProfile.role}</p>
            )}
            {selectedProfile.bio && (
              <p className={styles.modalBio}>{selectedProfile.bio}</p>
            )}
            <div className={styles.modalActions}>
              {selectedProfile.linkedin_url && (
                <a
                  href={selectedProfile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.modalViewProfile}
                >
                  View Profile
                </a>
              )}
              <button
                className={styles.modalClose}
                onClick={() => setSelectedProfile(null)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && galleryPhotos[lightboxIndex] && (
        <div
          className={styles.lightbox}
          onClick={() => setLightboxIndex(null)}
          role="presentation"
        >
          <button
            className={styles.lightboxClose}
            onClick={() => setLightboxIndex(null)}
            type="button"
          >
            ✕
          </button>
          {lightboxIndex > 0 && (
            <button
              className={styles.lightboxPrev}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(lightboxIndex - 1);
              }}
              type="button"
            >
              ←
            </button>
          )}
          {lightboxIndex < galleryPhotos.length - 1 && (
            <button
              className={styles.lightboxNext}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(lightboxIndex + 1);
              }}
              type="button"
            >
              →
            </button>
          )}
          <div
            className={styles.lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryPhotos[lightboxIndex].url}
              alt={galleryPhotos[lightboxIndex].caption || ''}
              className={styles.lightboxImage}
            />
            {galleryPhotos[lightboxIndex].caption && (
              <p className={styles.lightboxCaption}>
                {galleryPhotos[lightboxIndex].caption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditionDetailPage;
